import { Component, StrictMode, useCallback, useEffect, useState, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError, api, gastLinkFuer, getShareToken, merkeGastLink, setShareToken, type User } from './api';
import { fehlerAuffangen, fehlerMelden } from './fehlerMelden';
import { setzeInstallation } from './installation';
import { Editor } from './pages/Editor';
import { Einladung } from './pages/Einladung';
import { Login } from './pages/Login';
import { Projects } from './pages/Projects';
// The site's typeface, bundled rather than fetched from Google at runtime.
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import { useThema } from './thema';
import './styles.css';

/**
 * A three-screen app needs three routes, so it gets a router made of one
 * function rather than a dependency. `/p/<slug>` is the permalink: it is the
 * address people paste to each other, and it survives everything except
 * deleting the project.
 */
function currentRoute():
  | { name: 'projects' }
  | { name: 'editor'; slug: string }
  | { name: 'einladung' | 'passwort'; token: string } {
  const project = /^\/p\/([A-Za-z0-9_-]+)/.exec(location.pathname);
  if (project) return { name: 'editor', slug: project[1]! };
  // The two links that arrive by mail. They are the only routes that mean
  // something to somebody who is not signed in yet.
  const link = /^\/(einladung|passwort)\/([A-Za-z0-9_-]+)/.exec(location.pathname);
  if (link) return { name: link[1] as 'einladung' | 'passwort', token: link[2]! };
  return { name: 'projects' };
}

function App(): ReactElement {
  // Gesetzt hat das Thema schon das Skript in index.html, vor dem ersten Bild.
  // Hier haengt es nur noch am Geraet: steht die Wahl auf „wie das Gerät“ und
  // das iPhone schaltet abends um, zieht die App mit, ohne dass jemand neu
  // laden muss.
  useThema();
  const [user, setUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(false);
  const [route, setRoute] = useState(currentRoute);

  /** Der Server war nicht zu erreichen -- das ist keine Abmeldung (7.252). */
  const [getrennt, setGetrennt] = useState(false);
  const [nochmal, setNochmal] = useState(0);

  useEffect(() => {
    // A share token in the URL authenticates the whole session for that one
    // project; strip it from the address bar so it does not end up in a
    // screenshot or a pasted link.
    const params = new URLSearchParams(location.search);
    const share = params.get('share');
    const projekt = /^\/p\/([A-Za-z0-9_-]+)/.exec(location.pathname)?.[1];
    if (share) {
      setShareToken(share);
      if (projekt) merkeGastLink(projekt, share);
      params.delete('share');
      const query = params.toString();
      history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}`);
    } else if (projekt) {
      // Ohne Marke in der Adresse: die, die das Gerät zu diesem Projekt kennt
      // (aus dem Verlauf, vom Home-Bildschirm, nach geschlossenem Tab).
      const gemerkt = gastLinkFuer(projekt);
      if (gemerkt && gemerkt !== getShareToken()) setShareToken(gemerkt);
    }
  }, []);

  useEffect(() => {
    let aus = false;
    let wieder: ReturnType<typeof setTimeout> | null = null;
    const pruefen = (versuch: number) => {
      void api
        .me()
        .then(({ user, installation }) => {
          if (aus) return;
          setzeInstallation(installation);
          setUser(user);
          setGetrennt(false);
          setChecked(true);
        })
        .catch((err) => {
          if (aus) return;
          if (err instanceof ApiError && err.status === 401) {
            // Eine aufgehobene Marke, die nicht mehr gilt (widerrufen,
            // abgelaufen), wird vergessen -- sonst ginge sie bei jeder
            // Anfrage weiter mit.
            if (getShareToken()) setShareToken(null);
            setGetrennt(false);
            setChecked(true);
            return;
          }
          // Kein Netz, oder der Server startet gerade neu (beim Ausspielen
          // antwortet nginx eine Weile mit 502): das heisst nichts über die
          // Anmeldung. Bis hierher stand dann die Anmeldung da -- und ein Gast
          // ohne Konto kam von dort nicht wieder weg (Sascha, 16. Oktober).
          console.error(err);
          setGetrennt(true);
          wieder = setTimeout(() => pruefen(versuch + 1), Math.min(15000, 1000 * 2 ** Math.min(versuch, 4)));
        });
    };
    pruefen(0);
    return () => {
      aus = true;
      if (wieder) clearTimeout(wieder);
    };
  }, [nochmal]);

  useEffect(() => {
    const onPop = () => setRoute(currentRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((path: string) => {
    history.pushState(null, '', path);
    setRoute(currentRoute());
  }, []);

  if (!checked) {
    if (!getrennt) return <div className="page laedt">Einen Moment…</div>;
    return (
      <div className="page laedt" data-keine-verbindung>
        <p>Keine Verbindung zum Server. Es geht gleich von selbst weiter.</p>
        <button className="ghost small" onClick={() => setNochmal((n) => n + 1)}>
          Jetzt nochmal
        </button>
      </div>
    );
  }

  // Before the login check: somebody following an invitation has no account
  // yet, and somebody resetting a password cannot get past the login screen.
  if (route.name === 'einladung' || route.name === 'passwort') {
    return (
      <Einladung
        kind={route.name}
        token={route.token}
        onSignedIn={(signed) => {
          setUser(signed);
          setRoute({ name: 'projects' });
        }}
      />
    );
  }

  if (!user) return <Login onSignedIn={setUser} gastLink={route.name === 'editor'} />;

  if (route.name === 'editor') {
    return <Editor slug={route.slug} onBack={() => navigate('/')} />;
  }
  return (
    <Projects
      user={user}
      onOpen={(slug, blatt) => navigate(`/p/${slug}${blatt ? `?blatt=${blatt}` : ''}`)}
      onSignOut={() => void api.logout().then(() => setUser(null))}
      onUserChanged={setUser}
    />
  );
}

/**
 * Der Service Worker startet die App auch ohne Netz -- und nur im gebauten
 * Stand. Im Entwicklungsserver hätte er nichts zu tun als Ärger zu machen:
 * dort haben die Dateien keine Namen, die ihren Inhalt tragen.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

/**
 * **Das Auffangnetz** (22. Oktober, Veronika: „des lädt net -- Bildschirm
 * schwarz“). Wirft beim Zeichnen irgendwo etwas, räumt React die ganze Seite
 * ab, und übrig bleibt der dunkle Grund -- ohne ein Wort, ohne eine Spur in
 * der Diagnose. Hier wird der Fehler aufgefangen: er geht an den Server
 * (`fehlerMelden`), und auf dem Schirm steht, was passiert ist, mit einem Weg
 * zurück.
 */
class Auffangnetz extends Component<{ children: ReactNode }, { fehler: Error | null }> {
  override state: { fehler: Error | null } = { fehler: null };

  static getDerivedStateFromError(fehler: unknown): { fehler: Error } {
    return { fehler: fehler instanceof Error ? fehler : new Error(String(fehler)) };
  }

  override componentDidCatch(fehler: unknown, info: ErrorInfo): void {
    fehlerMelden('zeichnen', fehler, info.componentStack ?? '');
  }

  override render(): ReactNode {
    const { fehler } = this.state;
    if (!fehler) return this.props.children;
    return (
      <div className="page abgestuerzt" data-abgestuerzt>
        <p>Hier ist etwas schiefgegangen. Der Fehler ist gemeldet.</p>
        <p className="hint" style={{ wordBreak: 'break-word' }}>
          {fehler.message}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="primary small" onClick={() => location.reload()} data-abgestuerzt-neu>
            Neu laden
          </button>
          <button className="ghost small" onClick={() => location.assign('/')} data-abgestuerzt-uebersicht>
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }
}

fehlerAuffangen();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Auffangnetz>
      <App />
    </Auffangnetz>
  </StrictMode>,
);
